import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const DEFAULT_IGNORE_SELECTOR =
	'a,button,input,textarea,select,label,[role="button"],[role="link"],[data-no-drag]'

const DEFAULT_DRAG_POINTER_TYPES = ['mouse']

const clamp = (v, min, max) => Math.min(max, Math.max(min, v))

const prefersReducedMotion = () => {
	if (typeof window === 'undefined') return false
	return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
}

const easeOutCubic = t => 1 - Math.pow(1 - t, 3)

const animateScrollLeft = (el, to, ms = 260) => {
	if (!el) return
	if (prefersReducedMotion() || ms <= 0) {
		el.scrollLeft = to
		return
	}

	const from = el.scrollLeft
	const start = performance.now()

	const tick = now => {
		const t = clamp((now - start) / ms, 0, 1)
		el.scrollLeft = from + (to - from) * easeOutCubic(t)
		if (t < 1) requestAnimationFrame(tick)
	}

	requestAnimationFrame(tick)
}

const animateScrollTop = (el, to, ms = 260) => {
	if (!el) return
	if (prefersReducedMotion() || ms <= 0) {
		el.scrollTop = to
		return
	}

	const from = el.scrollTop
	const start = performance.now()

	const tick = now => {
		const t = clamp((now - start) / ms, 0, 1)
		el.scrollTop = from + (to - from) * easeOutCubic(t)
		if (t < 1) requestAnimationFrame(tick)
	}

	requestAnimationFrame(tick)
}

export const useHorizontalScroll = ({
	axis = 'x',
	dragToScroll = true,
	wheelMode = 'native',
	stepRatio = 0.6,

	dragThresholdPx = 8,
	ignoreDragFrom = DEFAULT_IGNORE_SELECTOR,

	dragPointerTypes = DEFAULT_DRAG_POINTER_TYPES,

	// arrows
	arrowMode = 'page', // 'page' | 'snap'
	arrowOverlapRatio = 0.12, // for page
	snapSelector = '> *',
	snapAlign = 'start', // 'start' | 'center'
	snapOffsetPx = 0, // number | 'auto'  (auto = padding-left of container)
	arrowAnimateMs = 260, // animation duration on arrows
} = {}) => {
	const ref = useRef(null)

	const [canLeft, setCanLeft] = useState(false)
	const [canRight, setCanRight] = useState(false)
	const [canUp, setCanUp] = useState(false)
	const [canDown, setCanDown] = useState(false)

	const stateRef = useRef({
		isDown: false,
		isDragging: false,
		suppressClick: false,
		startX: 0,
		startY: 0,
		startLeft: 0,
		startTop: 0,
		pointerId: null,
	})

	const getAutoOffset = useCallback(() => {
		const el = ref.current
		if (!el) return 0
		const cs = getComputedStyle(el)
		const pl = parseFloat(cs.paddingLeft || '0')
		return Number.isFinite(pl) ? pl : 0
	}, [])

	const resolvedSnapOffset = useMemo(() => {
		if (snapOffsetPx === 'auto') return getAutoOffset
		return null
	}, [snapOffsetPx, getAutoOffset])

	const recompute = useCallback(() => {
		const el = ref.current
		if (!el) return

		const {
			scrollLeft,
			scrollTop,
			scrollWidth,
			scrollHeight,
			clientWidth,
			clientHeight,
		} = el

		const EDGE_EPS = 2

		setCanLeft(scrollLeft > EDGE_EPS)
		setCanRight(scrollLeft + clientWidth < scrollWidth - EDGE_EPS)
		setCanUp(scrollTop > EDGE_EPS)
		setCanDown(scrollTop + clientHeight < scrollHeight - EDGE_EPS)
	}, [])

	const shouldIgnoreDragStart = useCallback(
		e => {
			if (!ignoreDragFrom) return false
			const target = e.target
			if (!(target instanceof Element)) return false
			return !!target.closest(ignoreDragFrom)
		},
		[ignoreDragFrom]
	)

	const isAllowedPointerType = useCallback(
		e => {
			const pt = e?.pointerType || 'mouse'
			if (!dragPointerTypes || dragPointerTypes.length === 0) return true
			return dragPointerTypes.includes(pt)
		},
		[dragPointerTypes]
	)

	const getArrowStepPage = useCallback(() => {
		const el = ref.current
		if (!el) return { stepX: 0, stepY: 0 }

		const pageX = el.clientWidth
		const pageY = el.clientHeight

		const stepX = Math.max(40, Math.round(pageX * (1 - arrowOverlapRatio)))
		const stepY = Math.max(40, Math.round(pageY * (1 - arrowOverlapRatio)))

		return { stepX, stepY }
	}, [arrowOverlapRatio])

	const getSnapTargets = useCallback(() => {
		const el = ref.current
		if (!el) return []
		try {
			return Array.from(el.querySelectorAll(snapSelector)).filter(
				n => n instanceof HTMLElement
			)
		} catch (_) {
			return Array.from(el.children).filter(n => n instanceof HTMLElement)
		}
	}, [snapSelector])

	const getSnapLeftForNode = useCallback(
		(node, el, offset) => {
			if (!node || !el) return 0

			let left = node.offsetLeft

			if (snapAlign === 'center') {
				const centerOffset = Math.round((el.clientWidth - node.clientWidth) / 2)
				left = left - centerOffset
			}

			left = left - offset

			return left
		},
		[snapAlign]
	)

	const scrollByDir = useCallback(
		(dir = 'right') => {
			const el = ref.current
			if (!el) return

			if (axis === 'y') {
				const { stepY } = getArrowStepPage()
				const maxTop = Math.max(0, el.scrollHeight - el.clientHeight)
				const nextTop = clamp(
					el.scrollTop + (dir === 'down' ? stepY : -stepY),
					0,
					maxTop
				)
				animateScrollTop(el, nextTop, arrowAnimateMs)
				return
			}

			const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth)

			// ✅ SNAP MODE
			if (arrowMode === 'snap') {
				const items = getSnapTargets()
				if (!items.length) return

				const offset =
					snapOffsetPx === 'auto'
						? resolvedSnapOffset?.() ?? 0
						: Number(snapOffsetPx) || 0

				const current = el.scrollLeft

				const positions = items
					.map(node => clamp(getSnapLeftForNode(node, el, offset), 0, maxLeft))
					.sort((a, b) => a - b)

				const EPS = 2
				let target = current

				if (dir === 'right') {
					target = positions.find(p => p > current + EPS) ?? maxLeft
				} else {
					for (let i = positions.length - 1; i >= 0; i--) {
						if (positions[i] < current - EPS) {
							target = positions[i]
							break
						}
					}
					if (current <= EPS) target = 0
				}

				animateScrollLeft(el, clamp(target, 0, maxLeft), arrowAnimateMs)
				return
			}

			// ✅ PAGE MODE
			const { stepX } = getArrowStepPage()
			const nextLeft = clamp(
				el.scrollLeft + (dir === 'right' ? stepX : -stepX),
				0,
				maxLeft
			)
			animateScrollLeft(el, nextLeft, arrowAnimateMs)
		},
		[
			axis,
			arrowMode,
			getArrowStepPage,
			getSnapTargets,
			getSnapLeftForNode,
			snapOffsetPx,
			resolvedSnapOffset,
			arrowAnimateMs,
		]
	)

	const onPointerDown = useCallback(
		e => {
			if (!dragToScroll) return
			if (!isAllowedPointerType(e)) return
			if (e.button !== null && e.button !== 0) return

			const el = ref.current
			if (!el) return
			if (shouldIgnoreDragStart(e)) return

			stateRef.current.isDown = true
			stateRef.current.isDragging = false
			stateRef.current.suppressClick = false

			stateRef.current.startX = e.clientX
			stateRef.current.startY = e.clientY
			stateRef.current.startLeft = el.scrollLeft
			stateRef.current.startTop = el.scrollTop
			stateRef.current.pointerId = e.pointerId

			try {
				el.setPointerCapture?.(e.pointerId)
			} catch (_) {}
		},
		[dragToScroll, shouldIgnoreDragStart, isAllowedPointerType]
	)

	const onPointerMove = useCallback(
		e => {
			if (!dragToScroll) return
			if (!isAllowedPointerType(e)) return

			const el = ref.current
			if (!el || !stateRef.current.isDown) return

			const dx = e.clientX - stateRef.current.startX
			const dy = e.clientY - stateRef.current.startY

			const absX = Math.abs(dx)
			const absY = Math.abs(dy)

			const AXIS_LOCK_RATIO = 1.2
			const isAxisStrong =
				axis === 'x'
					? absX > absY * AXIS_LOCK_RATIO
					: absY > absX * AXIS_LOCK_RATIO

			if (!stateRef.current.isDragging) {
				if (!isAxisStrong) return

				const axisDist = axis === 'x' ? absX : absY
				if (axisDist < dragThresholdPx) return

				stateRef.current.isDragging = true
				stateRef.current.suppressClick = true

				el.classList.add('isDragging')
			}

			e.preventDefault?.()

			if (axis === 'y') {
				el.scrollTop = stateRef.current.startTop - dy
				return
			}

			el.scrollLeft = stateRef.current.startLeft - dx
		},
		[axis, dragToScroll, dragThresholdPx, isAllowedPointerType]
	)

	const endDrag = useCallback(() => {
		const el = ref.current

		stateRef.current.isDown = false
		stateRef.current.pointerId = null

		if (el) el.classList.remove('isDragging')

		if (stateRef.current.suppressClick) {
			setTimeout(() => {
				stateRef.current.suppressClick = false
				stateRef.current.isDragging = false
			}, 0)
			return
		}

		stateRef.current.isDragging = false
	}, [])

	const onClickCapture = useCallback(e => {
		if (!stateRef.current.suppressClick) return
		e.preventDefault()
		e.stopPropagation()
	}, [])

	const onWheel = useCallback(
		e => {
			const el = ref.current
			if (!el) return
			if (wheelMode === 'native') return
			if (axis !== 'x') return

			const hasXOverflow = el.scrollWidth > el.clientWidth + 1
			if (!hasXOverflow) return

			const dx = e.deltaX
			const dy = e.deltaY

			if (wheelMode === 'shiftToScroll') {
				// Only scroll horizontally when Shift is pressed, using horizontal delta if present,
				// otherwise fall back to vertical delta.
				if (!e.shiftKey) return
				e.preventDefault()
				el.scrollLeft += Math.abs(dx) > 0 ? dx : dy
				return
			}

			if (wheelMode === 'autoToScroll') {
				// For horizontal scrollers, map vertical wheel movement to horizontal scroll
				// so that normal mouse wheel gestures also scroll the tab strip.
				let delta = dx
				if (axis === 'x') {
					const absX = Math.abs(dx)
					const absY = Math.abs(dy)
					// If vertical movement is stronger (typical mouse wheel), use dy instead.
					if (absY > absX) {
						delta = dy
					}
				}
				if (delta === 0) return
				e.preventDefault()
				el.scrollLeft += delta
			}
		},
		[axis, wheelMode]
	)

	useEffect(() => {
		const el = ref.current
		if (!el) return

		recompute()

		const onScroll = () => recompute()
		el.addEventListener('scroll', onScroll)

		const ro = new ResizeObserver(() => recompute())
		ro.observe(el)
		Array.from(el.children).forEach(n => ro.observe(n))

		const wheelOpts = { passive: false }
		el.addEventListener('wheel', onWheel, wheelOpts)

		window.addEventListener('pointerup', endDrag)
		window.addEventListener('pointercancel', endDrag)
		window.addEventListener('blur', endDrag)

		return () => {
			el.removeEventListener('scroll', onScroll)
			el.removeEventListener('wheel', onWheel, wheelOpts)
			window.removeEventListener('pointerup', endDrag)
			window.removeEventListener('pointercancel', endDrag)
			window.removeEventListener('blur', endDrag)
			ro.disconnect()
		}
	}, [recompute, onWheel, endDrag])

	return {
		ref,
		recompute,
		canLeft,
		canRight,
		canUp,
		canDown,
		scrollByDir,
		bind: {
			onPointerDown,
			onPointerMove,
			onPointerUp: endDrag,
			onPointerCancel: endDrag,
			onClickCapture,
		},
		axis,
	}
}
